/**
 * LanguageProvider compartido para todos los microservicios A365
 * 
 * Este contexto unificado reemplaza las múltiples implementaciones
 * duplicadas en landings y páginas, proporcionando una gestión
 * centralizada del idioma basada en URL y localStorage.
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { currentLanguage$ } from "../utils/language-utils";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage debe utilizarse dentro de un LanguageProvider');
  }
  return context;
};

/**
 * Configuración de estrategias de detección de idioma
 * Cada app puede usar la estrategia que mejor le convenga
 */
export const LANGUAGE_STRATEGIES = {
  // Estrategia para landings: lee del 3er segmento de la URL
  LANDING: 'landing',
  // Estrategia para páginas principales: lee del 1er segmento + localStorage
  PAGE: 'page',
  // Estrategia simple: solo localStorage
  STORAGE: 'storage'
};

const getLanguageFromUrl = (strategy = LANGUAGE_STRATEGIES.LANDING, i18nInstance = null) => {
  try {
    let languageCode;
    
    // Verificar si localStorage está disponible
    const isLocalStorageEnabled = () => {
      try {
        const key = '__storage__test';
        window.localStorage.setItem(key, null);
        window.localStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    };

    if (strategy === LANGUAGE_STRATEGIES.LANDING) {
      // Estrategia para landings (como landing-mx-jp, etc.)
      if (window.localStorage.getItem("lang")) {
        languageCode = window.localStorage.getItem("lang") === "pt" ? "br" : "ar";
      } else {
        const url = window.location.href;
        languageCode = url.split("/")[3];
      }
    } 
    else if (strategy === LANGUAGE_STRATEGIES.PAGE) {
      // Estrategia para páginas principales (como checkout, home, etc.)
      const url = window.location.pathname;
      
      if (isLocalStorageEnabled()) {
        if (localStorage.getItem("lang")) {
          languageCode = localStorage.getItem("lang") === "pt" 
            ? "br" 
            : url.split("/")[1] === "ar" ? "ar" : "es";
        } else {
          const geo = url.split("/")[1];
          languageCode = geo === "pt" ? "br" : geo;
        }
        // Guardar en localStorage para siguientes cargas
        localStorage.setItem("lang", languageCode === "br" ? "pt" : "es");
      } else {
        const geo = url.split("/")[1];
        languageCode = geo === "pt" ? "br" : geo;
      }
    }
    else if (strategy === LANGUAGE_STRATEGIES.STORAGE) {
      // Solo localStorage
      languageCode = window.localStorage.getItem("lang") || "es";
    }

    // Mapear códigos a locales completos
    switch (languageCode) {
      case "br":
        return "pt-BR";
      case "ar":
        return "es-AR";
      default:
        return "es";
    }
  } catch (error) {
    console.error('Error detectando idioma:', error);
    return "es"; // fallback
  }
};

export const LanguageProvider = ({ 
  children, 
  i18nInstance = null, 
  strategy = LANGUAGE_STRATEGIES.LANDING 
}) => {
  const [language, setLanguage] = useState(() => 
    getLanguageFromUrl(strategy, i18nInstance)
  );

  const changeLanguage = (newLanguage) => {
    if (i18nInstance && typeof i18nInstance.changeLanguage === 'function') {
      i18nInstance.changeLanguage(newLanguage);
    }
    setLanguage(newLanguage);
  };

  useEffect(() => {
    if (i18nInstance && typeof i18nInstance.changeLanguage === 'function') {
      i18nInstance.changeLanguage(language);
    }
  }, [language, i18nInstance]);

  // Subscribe to external language changes from currentLanguage$ observable
  useEffect(() => {
    const subscription = currentLanguage$.subscribe((newLanguage) => {
      if (newLanguage && newLanguage !== language) {
        console.log(`LanguageProvider: External language change detected: ${language} -> ${newLanguage}`);
        
        if (i18nInstance && typeof i18nInstance.changeLanguage === 'function') {
          i18nInstance.changeLanguage(newLanguage);
        }
        setLanguage(newLanguage);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [language, i18nInstance]);

  const contextValue = {
    language,
    setLanguage: changeLanguage,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Re-exportar el contexto para casos específicos que lo necesiten
export { LanguageContext };
